import type { FeatureFlagName } from '@ipfabric/utils/featureFlag';

const LOCAL_STORAGE_KEY = 'featureFlags';

type LocalFeatureFlags = Partial<Record<FeatureFlagName, boolean>>;

/**
 * Parse feature flags from localStorage.
 * Format: comma-separated list where `!FLAG` means disabled, `FLAG` means enabled.
 */
export const getLocalFeatureFlags = (): LocalFeatureFlags => {
  const flagsStr = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!flagsStr) {
    return {};
  }

  const flags = flagsStr.split(',').filter(Boolean);
  return flags.reduce<LocalFeatureFlags>((acc, flag) => {
    if (flag.startsWith('!')) {
      acc[flag.slice(1) as FeatureFlagName] = false;
    } else {
      acc[flag as FeatureFlagName] = true;
    }
    return acc;
  }, {});
};

/**
 * Save feature flags to localStorage.
 * Format: comma-separated list where `!FLAG` means disabled, `FLAG` means enabled.
 */
export const setLocalFeatureFlags = (flags: LocalFeatureFlags): void => {
  const entries = Object.entries(flags) as [FeatureFlagName, boolean][];
  const flagsStr = entries
    .map(([name, enabled]) => (enabled ? name : `!${name}`))
    .join(',');

  if (flagsStr) {
    localStorage.setItem(LOCAL_STORAGE_KEY, flagsStr);
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
};

/**
 * Toggle a single feature flag in localStorage.
 */
export const toggleLocalFeatureFlag = (
  flag: FeatureFlagName,
  enabled: boolean,
): void => {
  const currentFlags = getLocalFeatureFlags();
  setLocalFeatureFlags({ ...currentFlags, [flag]: enabled });
};

/**
 * Remove a feature flag override from localStorage (revert to server default).
 */
export const removeLocalFeatureFlag = (flag: FeatureFlagName): void => {
  const currentFlags = getLocalFeatureFlags();
  const { [flag]: _, ...rest } = currentFlags;
  setLocalFeatureFlags(rest);
};
